import React from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { ChartSimpleIcon } from '@revfluence/fresh-icons/solid/esm';
import { Card } from '@frontend/shadcn/components/ui/card';
import { Empty } from '@frontend/app/refresh-components';
import { useFragment, type FragmentType } from '@frontend/gql/social';
import { graphql } from '@frontend/gql/social';
import { formatNumberDefault } from '../../../utils/formatNumber';
import { GraphCardHeader, GraphCardTitle, GraphCardContent } from '../../../components/graph-card';
import MetricChangeIndicator from '../../../components/metric-change-indicator';
import { DateType } from '../../../types';

export const followerGrowthCardFragment = graphql(`
  fragment FollowerGrowthCard_TotalInsights on TotalInsights {
    followerGrowth
  }
`);

interface FollowerGrowthCardProps {
  className?: string;
  startDate?: string;
  endDate?: string;
  dateType?: DateType;
  data: FragmentType<typeof followerGrowthCardFragment> | null | undefined;
  previousPeriodData?: FragmentType<typeof followerGrowthCardFragment> | null | undefined;
}

interface FollowerGrowthData {
  date: string;
  followers: number;
  formattedDate: string;
}

// Demo data for the line chart (until we have time series data)
const demoData: FollowerGrowthData[] = [
  { date: '2024-02-01', followers: 1, formattedDate: 'Feb 1' },
  { date: '2024-02-08', followers: 4, formattedDate: 'Feb 8' },
  { date: '2024-02-15', followers: 10, formattedDate: 'Feb 15' },
  { date: '2024-02-22', followers: 7, formattedDate: 'Feb 22' },
  { date: '2024-02-29', followers: 2, formattedDate: 'Feb 29' },
  { date: '2024-03-07', followers: 1, formattedDate: 'Mar 7' },
  { date: '2024-03-14', followers: 9, formattedDate: 'Mar 14' },
  { date: '2024-03-21', followers: 8, formattedDate: 'Mar 21' },
  { date: '2024-03-28', followers: 4, formattedDate: 'Mar 28' },
  { date: '2024-04-04', followers: 0, formattedDate: 'Apr 4' },
  { date: '2024-04-11', followers: 6, formattedDate: 'Apr 11' },
  { date: '2024-04-18', followers: 3, formattedDate: 'Apr 18' },
];

const FollowerGrowthCard = React.forwardRef<HTMLDivElement, FollowerGrowthCardProps>(
  ({ className, startDate, endDate, dateType, data, previousPeriodData }, ref) => {
    // Extract follower growth data using the fragment
    const totalInsights = useFragment(followerGrowthCardFragment, data);
    const previousTotalInsights = useFragment(followerGrowthCardFragment, previousPeriodData);

    // Get current and previous period follower growth
    const currentFollowerGrowth = totalInsights?.followerGrowth ?? 0;
    const previousFollowerGrowth = previousTotalInsights?.followerGrowth ?? 0;

    // Determine if we have real data (non-zero current growth or previous growth)
    const hasRealData = currentFollowerGrowth !== 0 || previousFollowerGrowth !== 0;

    return (
      <Card ref={ref} className={className} role="region" aria-label="Follower growth over time">
        <GraphCardHeader className="border-b">
          <GraphCardTitle>Follower Growth</GraphCardTitle>
        </GraphCardHeader>
        <GraphCardContent className="pt-4 flex flex-col justify-between flex-1">
          <div className="mb-4 ml-12">
            <div
              className="font-sans text-[32px] font-medium text-gray-900 leading-[48px] mb-1"
              aria-live="polite"
              aria-atomic="true"
            >
              {formatNumberDefault(currentFollowerGrowth)}
            </div>
            <MetricChangeIndicator
              current={currentFollowerGrowth}
              previous={previousFollowerGrowth}
              startDate={startDate}
              endDate={endDate}
              dateType={dateType}
            />
            {!hasRealData && (
              <div className="text-xs text-muted-foreground mt-2 italic">
                ⚠️ No real follower growth data available for this period. Chart shows demo data only.
              </div>
            )}
            {hasRealData && (
              <div className="text-xs text-muted-foreground mt-2 italic">
                📊 Follower growth metrics are accurate. Chart visualization uses demo data until time series data is
                available.
              </div>
            )}
          </div>

          <div
            className="sm:h-[200px] h-[220px]"
            role="graphics-document"
            aria-label={`Line chart showing follower growth over time. Total growth: ${formatNumberDefault(currentFollowerGrowth)}`}
          >
            <ResponsiveContainer width="100%" height="100%">
              {demoData.length === 0 ? (
                <Empty
                  icon={ChartSimpleIcon}
                  title="No follower data available."
                  description="Follower growth data will appear here once available."
                  size="lg"
                />
              ) : (
                <LineChart data={demoData} aria-hidden="true" margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="formattedDate"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={10}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={10}
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value: number) => String(formatNumberDefault(value))}
                  />
                  <Tooltip
                    labelStyle={{ color: '#374151' }}
                    formatter={(value: number) => [formatNumberDefault(value), 'Follower Growth']}
                    labelFormatter={(date: string) => `Date: ${date}`}
                  />
                  <Line
                    type="linear"
                    dataKey="followers"
                    stroke="#4F7DBA"
                    strokeWidth={4}
                    dot={false}
                    activeDot={{ r: 6, stroke: '#4F7DBA', strokeWidth: 2 }}
                    role="graphics-symbol"
                    aria-label="Follower growth trend line"
                  />
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>

          {/* Screen reader accessible table */}
          <div className="sr-only">
            <table>
              <caption>Follower growth by time period</caption>
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Follower Growth</th>
                </tr>
              </thead>
              <tbody>
                {demoData.map((data) => (
                  <tr key={data.date}>
                    <th scope="row">{data.formattedDate}</th>
                    <td>{formatNumberDefault(data.followers)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary for screen readers */}
          <div className="sr-only" aria-live="polite">
            <h3>Follower Growth Summary:</h3>
            <ul>
              <li>Total follower growth: {formatNumberDefault(currentFollowerGrowth)}</li>
              {hasRealData && (
                <li>
                  Current period: {currentFollowerGrowth}, Previous period: {previousFollowerGrowth}
                </li>
              )}
              <li>
                Peak growth period:{' '}
                {
                  demoData.reduce((max, data) => (data.followers > max.followers ? data : max), demoData[0])
                    ?.formattedDate
                }
              </li>
            </ul>
          </div>
        </GraphCardContent>
      </Card>
    );
  },
);

FollowerGrowthCard.displayName = 'FollowerGrowthCard';

export default FollowerGrowthCard;
